import {
  matchPath,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Tab } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import { createHeaderTab } from '../../Layout/createHeaderTab';
import { PageRouteRef, getPageDescriptor } from '../utils';

export type UseTabsResult = {
  tabs: Tab[];
  selectedIndex: number;
};

type LooseRouteFunc = (params: Record<string, string | undefined>) => string;

export const useTabs = (tabsRouteRefs: PageRouteRef[]): UseTabsResult => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const search = searchParams.toString();

  const items = tabsRouteRefs.map(ref => {
    const descriptor = getPageDescriptor(ref);
    // eslint-disable-next-line react-hooks/rules-of-hooks -- callers gate tabs on values that are effectively stable for the component's lifetime (feature flags, permissions); toggling at runtime would require a remount
    const route = useRouteRef(ref) as LooseRouteFunc;
    const path = route(params);
    const to = search ? `${path}?${search}` : path;
    return {
      tab: createHeaderTab({
        id: descriptor.id,
        label: descriptor.defaultTab ?? descriptor.title,
        to,
      }),
      path,
      isDefault: descriptor.defaultTab !== undefined,
    };
  });

  const selectedIndex =
    [
      items.findIndex(({ path }) => matchPath({ path, end: true }, pathname)),
      items.findIndex(item => item.isDefault),
    ].find(index => index >= 0) ?? 0;

  return {
    tabs: items.map(item => item.tab),
    selectedIndex,
  };
};
